import { mapDatabaseStatusToDisplay } from './contractStatus';

export type SearchItemKind = 'metals' | 'electronics';

export interface SearchDialogBounds {
    left: number;
    top: number;
    width: number;
    height: number;
}

export const getSearchDialogBounds = (
    content: Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom'>,
    viewportWidth: number,
    viewportHeight: number,
    sidebarRight = 0,
): SearchDialogBounds => {
    const left = Math.max(0, sidebarRight, content.left);
    const top = Math.max(0, content.top);
    return {
        left,
        top,
        width: Math.max(0, Math.min(content.right, viewportWidth) - left),
        height: Math.max(0, Math.min(content.bottom, viewportHeight) - top),
    };
};

export type SearchNumericValue = string | number | null;

export interface AdvancedSearchItem {
    id: number;
    contractId: number | null;
    contractNumber: string | null;
    status: string | null;
    date: string | null;
    clientName: string | null;
    duration: number | null;
    metalType?: string | null;
    purity?: string | null;
    category?: number | null;
    description?: string | null;
    type?: string | null;
    unit: string | null;
    quantity: SearchNumericValue;
    unitPrice: SearchNumericValue;
    amountEur: SearchNumericValue;
    amountRon: SearchNumericValue;
}

export interface AdvancedSearchResults {
    metals: AdvancedSearchItem[];
    electronics: AdvancedSearchItem[];
}

export type SearchColumnKey = Exclude<keyof AdvancedSearchItem, 'id' | 'contractId'>;

export const searchItemColumns = (kind: SearchItemKind): SearchColumnKey[] => {
    const details: SearchColumnKey[] =
        kind === 'metals' ? ['metalType', 'purity', 'category'] : ['description', 'type'];
    return [
        'status',
        'contractNumber',
        'date',
        'clientName',
        ...details,
        'unit',
        'quantity',
        'unitPrice',
        'amountEur',
        'amountRon',
        'duration',
    ];
};

const isSearchDate = (value: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const date = new Date(value);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const isValidSearchPeriod = (startDate: string, endDate: string): boolean =>
    isSearchDate(startDate) && isSearchDate(endDate) && startDate <= endDate;

export const parseSearchWeight = (weight: string): number | null => {
    const normalized = weight.trim().replace(',', '.');
    if (normalized === '') return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : Number.NaN;
};

export const filterSearchItems = (
    items: AdvancedSearchItem[],
    kind: SearchItemKind,
    search: string,
    weight: string,
): AdvancedSearchItem[] => {
    const text = search.trim().toLocaleLowerCase();
    const grams = kind === 'metals' ? parseSearchWeight(weight) : null;
    return items.filter(item => {
        if (
            grams !== null &&
            (item.quantity === null ||
                Number(Number(item.quantity).toFixed(2)) !== Number(grams.toFixed(2)))
        )
            return false;
        return (
            !text ||
            searchItemColumns(kind).some(key =>
                searchCellText(item, key).toLocaleLowerCase().includes(text),
            )
        );
    });
};

export const formatSearchNumber = (value: unknown): string =>
    value === null || value === undefined || value === '' ? '' : Number(value).toFixed(2);

export const numericSearchColumns = new Set(['quantity', 'unitPrice', 'amountEur', 'amountRon']);

export const searchCellText = (item: AdvancedSearchItem, key: keyof AdvancedSearchItem): string => {
    if (key === 'status') return mapDatabaseStatusToDisplay(item.status);
    return numericSearchColumns.has(key) ? formatSearchNumber(item[key]) : String(item[key] ?? '');
};

const searchColumnWeights: Record<SearchColumnKey, number> = {
    status: 68,
    contractNumber: 105,
    date: 85,
    clientName: 150,
    metalType: 60,
    purity: 50,
    category: 50,
    description: 200,
    type: 38,
    unit: 34,
    quantity: 60,
    unitPrice: 70,
    amountEur: 70,
    amountRon: 70,
    duration: 38,
};

export const getSearchColumnWidths = (
    kind: SearchItemKind,
    availableWidth: number,
): Partial<Record<SearchColumnKey, number>> => {
    const columns = searchItemColumns(kind);
    const totalWeight = columns.reduce((total, key) => total + searchColumnWeights[key], 0);
    return Object.fromEntries(
        columns.map(key => [
            key,
            Math.floor((availableWidth * searchColumnWeights[key]) / totalWeight),
        ]),
    );
};

export const filterSearchColumns = (
    items: AdvancedSearchItem[],
    kind: SearchItemKind,
    filters: Record<string, string>,
): AdvancedSearchItem[] =>
    items.filter(item =>
        searchItemColumns(kind).every(key =>
            searchCellText(item, key)
                .toLowerCase()
                .includes((filters[key] ?? '').toLowerCase()),
        ),
    );
